import React, { useCallback, useEffect } from 'react';

import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import { debounce } from 'lodash';
import { useBoolean } from 'react-use';

import {
  ControlsContainer,
  BottomControlsContainer,
  ElapsedAndTotalTimeContainer,
  ControlsWrapper,
  HIDDEN_CONTROLS_CLASS_NAME,
} from './MobileControls.style';
import { mobileControlsEventsWrapper } from './MobileControls.utils';
import ElapsedAndTotalTime from '../ElapsedAndTotalTime';
import {
  getFullscreenButtonIcon,
  getOnPlayPauseClick,
  getPlayPauseIcon,
} from '../Mp3KbpPlayerControls/utils';
import PlayerButton from '../PlayerButton';
import { PLAYER_BUTTON_SIZE } from '../PlayerButton/PlayerButton.style';
import TimeSlider from '../TimeSlider';

const INACTIVITY_THRESHOLD_IN_MS = 3000;

interface Props {
  isPlaying: boolean;
  handlePlay: () => void;
  handlePause: () => void;
  handleRestartSong: () => void;
  isPlayNextSongDisabled: boolean;
  handlePlayNextSong: () => void;
  songDuration: number | null;
  songFutureTime: number | null;
  setSongFutureTime: (futureTime: number | null) => void;
  bufferedTimeRanges: TimeRanges | null;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  videoElement: HTMLVideoElement;
}

const MobileControls: React.FC<Props> = ({
  isPlaying,
  handlePlay,
  handlePause,
  handleRestartSong,
  isPlayNextSongDisabled,
  handlePlayNextSong,
  songDuration,
  songFutureTime,
  setSongFutureTime,
  bufferedTimeRanges,
  isFullscreen,
  toggleFullscreen,
  videoElement,
}) => {
  const [controlsVisible, toggleControlsVisible] = useBoolean(true);

  const hideControlsAfterInactivity = useCallback(
    debounce(() => toggleControlsVisible(false), INACTIVITY_THRESHOLD_IN_MS),
    [toggleControlsVisible],
  );
  const onControlClick = isPlaying ? hideControlsAfterInactivity : null;

  useEffect(() => {
    isPlaying && hideControlsAfterInactivity();
  }, [hideControlsAfterInactivity, isPlaying]);

  const onWrapperClick = () => {
    toggleControlsVisible();
    hideControlsAfterInactivity();
  };

  const PlayPauseIcon = getPlayPauseIcon(isPlaying);
  const onPlayPauseClick = getOnPlayPauseClick(isPlaying, handlePause, handlePlay);
  const FullscreenButtonIcon = getFullscreenButtonIcon(isFullscreen);

  return (
    <ControlsWrapper onClick={onWrapperClick}>
      <ControlsContainer className={controlsVisible ? '' : HIDDEN_CONTROLS_CLASS_NAME}>
        <PlayerButton
          Icon={SkipPreviousIcon}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            mobileControlsEventsWrapper(onControlClick, handleRestartSong)();
          }}
        />
        <PlayerButton
          Icon={PlayPauseIcon}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            mobileControlsEventsWrapper(
              isPlaying ? hideControlsAfterInactivity.cancel : hideControlsAfterInactivity,
              onPlayPauseClick,
            )();
          }}
          mobileSize={PLAYER_BUTTON_SIZE.BIG}
        />
        <PlayerButton
          Icon={SkipNextIcon}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            mobileControlsEventsWrapper(onControlClick, handlePlayNextSong)();
          }}
          isDisabled={isPlayNextSongDisabled}
        />
        <BottomControlsContainer
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            mobileControlsEventsWrapper(onControlClick)();
          }}
        >
          <ElapsedAndTotalTimeContainer>
            <ElapsedAndTotalTime songDuration={songDuration} videoElement={videoElement} />
          </ElapsedAndTotalTimeContainer>
          {songDuration && (
            <TimeSlider
              songDuration={songDuration}
              songFutureTime={songFutureTime}
              setSongFutureTime={setSongFutureTime}
              bufferedTimeRanges={bufferedTimeRanges}
              videoElement={videoElement}
            />
          )}
          <PlayerButton
            Icon={FullscreenButtonIcon}
            onClick={toggleFullscreen}
            mobileSize={PLAYER_BUTTON_SIZE.SMALL}
          />
        </BottomControlsContainer>
      </ControlsContainer>
    </ControlsWrapper>
  );
};

export default MobileControls;
